import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RTAApiClient } from '../../../shared/services/rta-api.service';

declare var CURRENT_USER_ID: number;

export interface ApprovalActionInfo {
  taskId: number;
  orderId: number;
  action: string;
  processName: string;
  orderDescription: string;
  subPsl: string;
  submittedBy: string;
  productLine: string;
  isValid: boolean;
  errorMessage?: string;
}

@Component({
  selector: 'app-approval-action',
  templateUrl: './approval-action.component.html',
  styleUrls: ['./approval-action.component.scss'],
  standalone: false
})
export class ApprovalActionComponent implements OnInit {
  token: string = '';
  info: ApprovalActionInfo | null = null;
  loading = true;
  submitting = false;
  completed = false;
  error: string | null = null;
  reason: string = '';
  resultMessage: string = '';
  resultSuccess = false;

  get isReject(): boolean {
    return this.info?.action?.toLowerCase() === 'reject';
  }

  get actionLabel(): string {
    return this.isReject ? 'Reject' : 'Approve';
  }

  get actionPastTense(): string {
    return this.isReject ? 'Rejected' : 'Approved';
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: RTAApiClient
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    if (!this.token) {
      this.error = 'No approval token provided.';
      this.loading = false;
      return;
    }
    this.loadInfo();
  }

  loadInfo(): void {
    this.loading = true;
    this.api.getApprovalActionInfo(this.token).subscribe({
      next: (info) => {
        this.info = info;
        if (!info.isValid) {
          this.error = info.errorMessage || 'This approval link is invalid or has expired.';
        }
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Unable to load approval information. The link may be invalid or expired.';
        this.loading = false;
      }
    });
  }

  submit(): void {
    if (this.isReject && !this.reason.trim()) {
      return; // Reason is required for rejection
    }

    this.submitting = true;
    this.api.executeApprovalAction(this.token, CURRENT_USER_ID, this.isReject ? this.reason : undefined).subscribe({
      next: (result) => {
        this.submitting = false;
        this.completed = true;
        this.resultSuccess = result.status ?? false;
        this.resultMessage = result.message || (result.status
          ? `Order has been ${this.actionPastTense.toLowerCase()} successfully.`
          : 'Action could not be completed.');
      },
      error: (err) => {
        this.submitting = false;
        this.completed = true;
        this.resultSuccess = false;
        this.resultMessage = 'An error occurred while processing your request. Please try again or contact support.';
      }
    });
  }
}
